import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEOHead from '../components/SEOHead';
import FeaturedWork from '../components/FeaturedWork';
import TeluguMoviesSection from '../components/TeluguMoviesSection';
import BeforeAfterSection from '../components/BeforeAfterSection';
import PipelineSection from '../components/PipelineSection';
import SearchIntentDirectory from '../components/SearchIntentDirectory';
import FAQSection from '../components/FAQSection';
import FooterCTA from '../components/FooterCTA';

const CALENDLY_URL = 'https://calendly.com/jagan-motionx/30min';

const PILLAR_CONFIGS = {
  'ai-vfx': {
    title: 'AI VFX Studio for Feature Films & Commercials | Green Screen & Relighting | MotionX Studios',
    description:
      'Enterprise Hybrid AI VFX studio in Hyderabad & worldwide. We deliver green-screen replacement, AI set & crowd extension, Beeble SwitchX relighting, and 2K/4K ProRes 4444 VFX for Telugu feature films (#CULT, HONEY, MANODU G.O.A.T) and broadcast commercials.',
    canonicalPath: '/ai-vfx',
    filterPillar: 'brand_work',
    eyebrow: 'Hybrid Live-Action + AI VFX · Feature Films & Broadcast Commercials',
    headline: 'AI VFX Studio for Feature Films & Commercials.',
    subheadline:
      'From 40,000-seat stadium extensions and Telugu theatrical feature films (#CULT, HONEY, MANODU G.O.A.T) to Beeble SwitchX relighting and custom ComfyUI 4K upscaling — we deliver cinema-grade ProRes 4444 VFX shots at 3–5× traditional speed.',
    highlights: [
      { label: 'Delivery Format', value: 'ProRes 4444 · 2K / 4K' },
      { label: 'Core Engine', value: 'Seedance 2.0 · Beeble · ComfyUI' },
      { label: 'Feature Credits', value: '#CULT · HONEY · MANODU G.O.A.T' },
      { label: 'IP & Security', value: '100% Client-Owned · NDA' },
    ],
  },
  'ai-commercials-ugc': {
    title: 'AI Commercial Production Company & Brand Film Agency | MotionX Studios',
    description:
      'Full-service AI commercial production company and hybrid ad agency crafting broadcast TVCs, automotive commercials, luxury brand films, and 100% AI spec campaigns at 3–5x speed.',
    canonicalPath: '/ai-commercials-ugc',
    filterPillar: 'brand_work',
    eyebrow: 'AI Commercial Production · TVCs · Brand Films · Spec Campaigns',
    headline: 'AI Commercial Production Company & Brand Film Studio.',
    subheadline:
      'We partner with brands and agencies across the US, UK, UAE, Bahrain, and India to produce television commercials (BKIC Insurance, Alcobrew BHRUM, Seef Properties, XPENG G9) and 100% AI-generated concept-to-master spots (Sony, Haute Couture, Fevi Stick).',
    highlights: [
      { label: 'Commercials Shipped', value: '15+ Brand & Spec Spots' },
      { label: 'Turnaround', value: '1–3 Weeks End-to-End' },
      { label: 'Engines', value: '100% AI & Hybrid VFX' },
      { label: 'Cost Advantage', value: '60–80% vs Traditional' },
    ],
  },
  'ai-ugc-agency': {
    title: 'AI UGC Agency & D2C E-Commerce Product Video Production | MotionX Studios',
    description:
      'Done-for-you AI UGC agency and D2C e-commerce product video studio. We produce high-converting 9:16 AI creator ads and photoreal product videos for footwear, jewellery, fashion, and fintech brands on Meta, TikTok, YouTube, Amazon & Shopify.',
    canonicalPath: '/ai-ugc-agency',
    filterPillar: 'ai_ugc',
    eyebrow: 'Done-For-You AI UGC Ads · D2C E-Commerce Product Videos · 9:16 Performance Creative',
    headline: 'AI UGC Agency & D2C Product Video Studio.',
    subheadline:
      'Stop shipping physical samples and waiting weeks for creator shoots. We turn your product references into high-converting 9:16 AI UGC creator ads and macro e-commerce product films — proven across Vendoz Bridal Sneakers, Zoom Shoes, MHPU Handbags, Fine Jewellery, and Fintech campaigns.',
    highlights: [
      { label: 'Ad Formats', value: '9:16 Reels/TikTok & 16:9 PDP' },
      { label: 'Industries', value: 'Footwear · Jewellery · Fashion · Apps' },
      { label: 'Turnaround', value: 'Days, Not Weeks' },
      { label: 'Physical Shoot Needed', value: 'Zero — 100% AI-Native' },
    ],
  },
  'micro-drama-production': {
    title: 'Vertical Micro-Drama Production Company (9:16 Series Studio) | MotionX Studios',
    description:
      'Premier 9:16 vertical micro-drama production company producing serialized short drama episodes for DramaBox, Dashverse, ReelShort, and mobile-first platforms at 3–5x speed.',
    canonicalPath: '/micro-drama-production',
    filterPillar: 'brand_work',
    eyebrow: '9:16 Vertical Micro-Drama Studio · Serialized Mobile Entertainment',
    headline: 'Vertical Micro-Drama & 9:16 Series Production Studio.',
    subheadline:
      'Engineered for the $11B mobile-first vertical drama market. We produce commissioned 9:16 micro-drama series with locked character consistency and cinematic VFX for leading platforms including DramaBox (Anna — Shadows of Deception, Iron Horizon — Siege Protocol) and Dashverse (Chronicles).',
    highlights: [
      { label: 'Platform Partners', value: 'DramaBox & Dashverse' },
      { label: 'Framing', value: 'Native 9:16 Mobile Cinema' },
      { label: 'Episode Velocity', value: '3–5× Faster Turnaround' },
      { label: 'Genres', value: 'Action · Thriller · Fantasy · Romance' },
    ],
  },
  'ai-filmmaking': {
    title: 'AI Filmmaking Studio, Original Short Films & AI Music Video Production | MotionX Studios',
    description:
      'AI filmmaking studio and music video production company creating flagship AI short films (Ashes of Fire, Jatayu, Flamingos, Mental), official 4K music videos (Aasmaan, Run), and hybrid feature films (RUDRA).',
    canonicalPath: '/ai-filmmaking',
    filterPillar: 'originals',
    eyebrow: 'AI Filmmaking Studio · Original Cinema · Official Music Videos',
    headline: 'AI Filmmaking, Original Cinema & Music Video Studio.',
    subheadline:
      'From our flagship action-thriller Ashes of Fire and mythological epic Jatayu to surreal shorts (Flamingos, Mental), official music videos (Aasmaan, Run), and our 90+ minute hybrid feature film RUDRA — we build theatrical worlds at the speed of imagination.',
    highlights: [
      { label: 'Flagship Original', value: 'Ashes of Fire ★' },
      { label: 'Feature Film', value: 'RUDRA (90+ Min Hybrid)' },
      { label: 'Music Videos', value: 'Aasmaan & Run (4K)' },
      { label: 'IP Ownership', value: '100% Studio & Client IP' },
    ],
  },
  work: {
    title: 'AI Video Production Portfolio (27 Commercials, AI UGC, Micro-Dramas & Films) | MotionX Studios',
    description:
      'Explore 27 verified AI commercials, D2C AI UGC product ads, 9:16 vertical micro-dramas, Telugu feature film VFX credits, music videos, and original films by MotionX Studios.',
    canonicalPath: '/work',
    filterPillar: 'all',
    eyebrow: 'Complete Studio Archive · 27 Films, Commercials & AI UGC Spots',
    headline: 'MotionX Production Archive.',
    subheadline:
      'Browse our full catalog of commissioned brand commercials, D2C AI UGC product ads, DramaBox & Dashverse vertical micro-dramas, 100% AI spec spots, Telugu feature film VFX credits, and MotionX Originals.',
    highlights: [
      { label: 'Brand & Micro-Dramas', value: '9 Client Productions' },
      { label: 'AI UGC & Product Ads', value: '6 D2C Campaigns' },
      { label: '100% AI Spec Ads', value: '6 Concept Spots' },
      { label: 'Originals & Feature VFX', value: '6 Originals + 4 Features' },
    ],
  },
};

export default function PillarLandingPage({ pillarKey = 'work' }) {
  const cfg = PILLAR_CONFIGS[pillarKey] || PILLAR_CONFIGS.work;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pillarKey]);

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEOHead
        title={cfg.title}
        description={cfg.description}
        canonicalPath={cfg.canonicalPath}
        filterPillar={cfg.filterPillar}
      />
      <Navigation />

      <header className="pt-28 pb-16 sm:pt-36 sm:pb-20 px-5 sm:px-10 lg:px-16 xl:px-24 border-b border-white/[0.08]">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#E50914]" />
          <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#E50914]">
            {cfg.eyebrow}
          </span>
        </div>

        <h1 className="text-white font-normal tracking-[-0.035em] leading-[1.03] text-[clamp(2rem,4.4vw,4.25rem)] max-w-4xl">
          {cfg.headline}
        </h1>

        <p className="text-[#9ca3af] text-sm sm:text-base md:text-[17px] max-w-3xl leading-relaxed mt-5">
          {cfg.subheadline}
        </p>

        <div className="flex flex-wrap gap-3 mt-8">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#E50914] hover:bg-[#ff1a25] text-white px-6 py-3 rounded-full text-[13px] font-medium shadow-[0_0_24px_rgba(229,9,20,0.35)] no-underline"
          >
            Book a Creative Call →
          </a>
          <Link
            to="/case-studies/stadium-vfx"
            className="border border-white/20 text-white/85 px-5 py-3 rounded-full text-[13px] hover:bg-white hover:text-black transition-all no-underline"
          >
            Read Stadium VFX Case Study
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12 pt-8 border-t border-white/[0.08]">
          {cfg.highlights.map((h) => (
            <div key={h.label} className="bg-[#09090b] border border-white/[0.08] rounded-xl p-5">
              <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-[#767d88]">
                {h.label}
              </p>
              <p className="text-lg sm:text-xl font-medium tracking-[-0.02em] text-white mt-1.5">
                {h.value}
              </p>
            </div>
          ))}
        </div>
      </header>

      <main>
        <FeaturedWork initialCategory={cfg.filterPillar} />
        <TeluguMoviesSection />
        <BeforeAfterSection />
        <PipelineSection />
        <SearchIntentDirectory />
        <FAQSection />
      </main>

      <FooterCTA />
    </div>
  );
}
